import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Description as A4Icon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

export type PrintType = 'a4' | 'thermal';

interface PrintOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  onPrint: (printType: PrintType) => void;
  title?: string;
  documentType?: 'receipt' | 'warranty' | 'invoice';
}

const PrintOptionsDialog: React.FC<PrintOptionsDialogProps> = ({
  open,
  onClose,
  onPrint,
  title = "خيارات الطباعة",
  documentType = "receipt"
}) => {
  const [selectedType, setSelectedType] = useState<PrintType>('a4');
  const [defaultPrintType, setDefaultPrintType] = useState<PrintType>('a4');

  useEffect(() => {
    // تحميل الإعدادات المحفوظة
    const savedPrintType = localStorage.getItem('default_print_type') as PrintType;
    if (savedPrintType && (savedPrintType === 'a4' || savedPrintType === 'thermal')) {
      setDefaultPrintType(savedPrintType);
      setSelectedType(savedPrintType);
    }
  }, []);

  const handlePrint = () => {
    // حفظ الاختيار كافتراضي
    localStorage.setItem('default_print_type', selectedType);
    onPrint(selectedType);
    onClose();
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value as PrintType);
  };

  const getDocumentTypeInfo = () => {
    switch (documentType) {
      case 'receipt':
        return {
          title: 'إيصال استلام',
          description: 'إيصال استلام الجهاز للصيانة',
          a4Description: 'طباعة على ورق A4 مع تصميم مفصل',
          thermalDescription: 'طباعة على طابعة حرارية بحجم صغير'
        };
      case 'warranty':
        return {
          title: 'شهادة ضمان',
          description: 'شهادة ضمان على الصيانة المنجزة',
          a4Description: 'طباعة على ورق A4 مع تفاصيل الضمان',
          thermalDescription: 'طباعة على طابعة حرارية مبسطة'
        };
      case 'invoice':
        return {
          title: 'فاتورة',
          description: 'فاتورة الخدمات المقدمة',
          a4Description: 'طباعة على ورق A4 مع تفاصيل الفاتورة',
          thermalDescription: 'طباعة على طابعة حرارية مختصرة'
        };
      default:
        return {
          title: 'مستند',
          description: 'مستند للطباعة',
          a4Description: 'طباعة على ورق A4',
          thermalDescription: 'طباعة على طابعة حرارية'
        };
    }
  };

  const docInfo = getDocumentTypeInfo();

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon />
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {docInfo.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {docInfo.description}
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
            اختر نوع الطباعة
          </FormLabel>
          
          <RadioGroup
            value={selectedType}
            onChange={handleTypeChange}
            sx={{ gap: 2 }}
          >
            {/* خيار A4 */}
            <Paper 
              elevation={selectedType === 'a4' ? 3 : 1}
              sx={{ 
                p: 2, 
                border: selectedType === 'a4' ? '2px solid' : '1px solid',
                borderColor: selectedType === 'a4' ? 'primary.main' : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  elevation: 2,
                  borderColor: 'primary.light'
                }
              }}
              onClick={() => setSelectedType('a4')}
            >
              <FormControlLabel
                value="a4"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <A4Icon color="primary" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" component="div">
                        طباعة A4
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {docInfo.a4Description}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                        مناسب للطباعة على الطابعات العادية
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Paper>

            {/* خيار الطابعة الحرارية */}
            <Paper 
              elevation={selectedType === 'thermal' ? 3 : 1}
              sx={{ 
                p: 2, 
                border: selectedType === 'thermal' ? '2px solid' : '1px solid',
                borderColor: selectedType === 'thermal' ? 'primary.main' : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  elevation: 2,
                  borderColor: 'primary.light'
                }
              }}
              onClick={() => setSelectedType('thermal')}
            >
              <FormControlLabel
                value="thermal"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" component="div">
                        طابعة حرارية
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {docInfo.thermalDescription}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                        مناسب للطابعات الحرارية الصغيرة
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>ملاحظة:</strong> سيتم حفظ اختيارك كافتراضي للطباعات القادمة
          </Typography>
        </Alert>

        <Box sx={{ 
          bgcolor: 'grey.50', 
          p: 2, 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary">
            <strong>نصائح:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
            • <strong>A4:</strong> مناسب للطباعة على الطابعات العادية، يحتوي على تفاصيل أكثر
            <br />
            • <strong>حرارية:</strong> مناسب للطابعات الحرارية، تصميم مضغوط وسريع
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          إلغاء
        </Button>
        <Button 
          onClick={handlePrint} 
          variant="contained"
          startIcon={<PrintIcon />}
          sx={{ minWidth: 120 }}
        >
          طباعة
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintOptionsDialog;

